import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../Button';
import { bookFamilyPortalSlot } from '../../data/familyPortal';
import {
  familyPortalRefusal, familyPortalSchedulePath, familyPortalWord as w,
} from '../../domain/familyPortal';
import { decimalSeparatorFor, formatLongDate, formatTime, studioFormatters } from '../../domain/locale';
import { formatCredits } from '../../domain/makeup';
import { zoneAbbreviation } from '../../domain/timeZone';
import { usePortalSlots } from '../../hooks/usePortalSlots';
import p from '../../pages/PortalPage.module.css';
import s from './FindAndBook.module.css';

// /portal/book (#773): MMS's Calendar → Find & Book, Booking Details and
// "Booking Complete!".
//
// MMS draws the open slots on a month grid, each "(0/1)" with a make-up icon
// where the slot needs a credit; choosing one opens Booking Details — a
// banner ("You have 2.5 make-up credits with this teacher", or "This booking
// will be reflected on your next invoice"), the date, time, category, "0/1
// Spots Taken", "Lesson cost: 0.5 Credits" or "Free", Cancel and Book Now —
// and Book Now books at once: "Booking Complete!", My Schedule and Done.
//
// Here the slots are an agenda over the whole booking window, day by day, as
// the Schedule is (PortalPage.jsx says why), and Booking Details opens in
// place of the list rather than as a side drawer, which a phone has no room
// for. A login is a family's, so Booking Details asks which student — MMS's
// child picker — and says beside a student who cannot take the slot why not.
// Everything the server decides (what is bookable, the price, the credit)
// comes from it (functions/portal/book.js); this only draws it, and asks
// again after a booking or a refusal.
//
// Every time is on the family's clock and labelled with its zone; no `Date`
// is built from a slot.

// Why a student cannot take a slot, in a few words beside their name.
const NOT_ELIGIBLE = {
  no_credit: 'notEligibleCredit',
  already_booked: 'notEligibleBooked',
  book_conflict: 'notEligibleConflict',
  credit_slot_shared: 'notEligibleShared',
};
const whyNot = (reason) => (NOT_ELIGIBLE[reason] ? w(NOT_ELIGIBLE[reason]) : familyPortalRefusal(reason));

export default function FindAndBook({ choice, student = 'all', onBooked = () => {} }) {
  const { answer, error, loading, retry } = usePortalSlots(choice);
  const [openId, setOpenId] = useState(null);
  const [done, setDone] = useState(null);

  if (error) {
    return (
      <section className={p.panel}>
        <p className={p.body}>{error.message}</p>
        {error.refusal === 'no_access' || error.refusal === 'closed' ? null : (
          <div className={p.form}><Button onClick={retry}>{w('retry')}</Button></div>
        )}
      </section>
    );
  }
  if (loading || !answer) return <section className={p.panel}><p className={p.body}>{w('loading')}</p></section>;
  if (!answer.bookingOn) {
    return <section className={p.panel}><p className={p.body}>{familyPortalRefusal('booking_off')}</p></section>;
  }

  if (done) {
    return (
      <BookingComplete
        answer={answer}
        done={done}
        onDone={() => { setDone(null); setOpenId(null); retry(); }}
      />
    );
  }

  const open = openId ? answer.slots.find((x) => x.id === openId) : null;
  if (open) {
    return (
      <BookingDetails
        key={open.id}
        answer={answer}
        slot={open}
        choice={choice}
        preferred={student}
        onCancel={() => setOpenId(null)}
        onBooked={(result) => { setDone({ slot: open, result }); onBooked(result); }}
        onStale={() => { setOpenId(null); retry(); }}
      />
    );
  }

  const shown = student === 'all'
    ? answer.slots
    : answer.slots.filter((x) => x.students.some((j) => j.id === student && j.reason !== 'already_booked'));
  return (
    <section className={p.panel}>
      <h2 className={p.panelTitle}>{w('navFindBook')}</h2>
      <p className={p.hint}>{w('findBookLead')}</p>
      {answer.family?.timeZone ? <p className={p.hint}>{w('timesIn')} {answer.family.timeZone.replace(/_/g, ' ')}.</p> : null}
      {shown.length === 0 ? <p className={p.body}>{w('noSlots')}</p> : <SlotList slots={shown} answer={answer} onOpen={setOpenId} />}
    </section>
  );
}

// The slot's time on the family's clock, its zone named.
function when(slot, answer) {
  const locale = answer.studio?.locale || undefined;
  const zone = answer.family?.timeZone;
  const label = zone ? zoneAbbreviation(zone, slot.date, slot.startTime, locale || 'en-US') : '';
  return `${formatTime(slot.startTime, locale)} – ${formatTime(slot.endTime, locale)}${label ? ` ${label}` : ''}`;
}

const spots = (slot) => `${slot.spotsTaken}/${slot.maxStudents} ${w('spotsTaken')}`;

function SlotList({ slots, answer, onOpen }) {
  const locale = answer.studio?.locale || undefined;
  const days = [];
  for (const slot of slots) {
    const last = days[days.length - 1];
    if (last && last.date === slot.date) last.slots.push(slot);
    else days.push({ date: slot.date, slots: [slot] });
  }
  return (
    <ol className={p.days}>
      {days.map((d) => (
        <li key={d.date} className={p.day}>
          <div className={p.dayTitle}>{formatLongDate(d.date, locale)}</div>
          <ul className={p.lessons}>
            {d.slots.map((slot) => (
              <li key={slot.id}>
                <button type="button" className={s.slot} onClick={() => onOpen(slot.id)}>
                  <span className={p.lessonWhen}>
                    {when(slot, answer)}
                    {slot.needsCredit ? <span className={s.creditBadge}>{w('needsCredit')}</span> : null}
                  </span>
                  <span className={p.lessonWho}>{[slot.category, spots(slot)].filter(Boolean).join(' · ')}</span>
                  {slot.description ? <span className={p.lessonMore}>{slot.description}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
}

// What a student would pay for this slot, as MMS's "Lesson cost" line says it.
function costOf(judged, answer) {
  const locale = answer.studio?.locale || undefined;
  if (!judged?.ok) return null;
  if (judged.credit) return `${formatCredits(judged.credit.credits, decimalSeparatorFor(locale))} ${w('creditsUnit')}`;
  if (judged.priceMinor === 0) return w('free');
  if (Number.isFinite(judged.priceMinor)) return studioFormatters(locale).formatMoney(judged.priceMinor);
  return null;
}

function SlotFacts({ slot, answer }) {
  const locale = answer.studio?.locale || undefined;
  return (
    <div className={s.facts}>
      <span className={s.fact}>{formatLongDate(slot.date, locale)}</span>
      <span className={s.fact}>{when(slot, answer)}</span>
      {slot.category ? <span className={s.fact}>{slot.category}</span> : null}
      <span className={s.fact}>{spots(slot)}</span>
    </div>
  );
}

function BookingDetails({ answer, slot, choice, preferred, onCancel, onBooked, onStale }) {
  const byId = useMemo(() => new Map(answer.students.map((x) => [x.id, x])), [answer.students]);
  const firstOk = slot.students.find((j) => j.ok)?.id || null;
  const initial = slot.students.some((j) => j.id === preferred && j.ok) ? preferred : firstOk;
  const [chosen, setChosen] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const locale = answer.studio?.locale || undefined;
  const judged = slot.students.find((j) => j.id === chosen) || null;
  const who = byId.get(chosen);
  const cost = costOf(judged, answer);

  const banner = slot.needsCredit
    ? (who ? `${w('creditsBannerPrefix')} ${formatCredits(who.credits?.available ?? null, decimalSeparatorFor(locale))} ${w('creditsBannerSuffix')}` : null)
    : w('invoiceBanner');

  const book = () => {
    if (!chosen || busy) return;
    setBusy(true);
    setFailure(null);
    bookFamilyPortalSlot({
      studioUid: choice.studioUid, familyId: choice.familyId, studentId: chosen, occurrenceId: slot.id,
    }).then(
      (result) => { setBusy(false); onBooked(result); },
      (err) => {
        setBusy(false);
        setFailure({ message: err?.message || familyPortalRefusal('failed'), refusal: err?.refusal || null });
      },
    );
  };

  // A refusal that says the list was out of date — taken, gone, now too
  // soon — sends the family back to a fresh one; anything else stays here.
  const stale = ['slot_full', 'slot_gone', 'book_too_soon', 'book_too_far', 'booking_off'].includes(failure?.refusal);

  return (
    <section className={p.panel}>
      <h2 className={p.panelTitle}>{w('bookingDetails')}</h2>
      {banner ? <p className={s.banner}>{banner}</p> : null}
      <SlotFacts slot={slot} answer={answer} />
      {slot.description ? <p className={p.hint}>{slot.description}</p> : null}

      <fieldset className={s.who}>
        <legend className={s.legend}>{w('bookFor')}</legend>
        {slot.students.map((j) => {
          const x = byId.get(j.id);
          return (
            <label key={j.id} className={j.ok ? s.choice : s.choiceOff}>
              <input
                type="radio"
                name="portal-book-student"
                value={j.id}
                checked={chosen === j.id}
                disabled={!j.ok || busy}
                onChange={() => setChosen(j.id)}
              />
              <span>{x?.name || x?.firstName || ''}</span>
              {j.ok ? null : <span className={s.why}>{whyNot(j.reason)}</span>}
            </label>
          );
        })}
      </fieldset>

      {cost ? (
        <div className={s.cost}>
          <span>{w('lessonCost')}</span>
          <span className={s.costValue}>{cost}</span>
        </div>
      ) : null}

      {failure ? <p className={p.error} role="alert">{failure.message}</p> : null}

      <div className={s.actions}>
        {stale ? (
          <Button variant="secondary" onClick={onStale}>{w('navFindBook')}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onCancel} disabled={busy}>{w('cancel')}</Button>
            <Button onClick={book} loading={busy} disabled={!judged?.ok}>{busy ? w('bookingNow') : w('bookNow')}</Button>
          </>
        )}
      </div>
    </section>
  );
}

function BookingComplete({ answer, done, onDone }) {
  const locale = answer.studio?.locale || undefined;
  const { result } = done;
  const slot = { ...done.slot, ...(result?.slot || {}) };
  const who = answer.students.find((x) => x.id === result?.studentId);
  return (
    <section className={p.panel}>
      <h2 className={s.complete}>{w('bookingComplete')}</h2>
      <p className={p.body}>{who?.name || ''}</p>
      <SlotFacts slot={{ ...slot, spotsTaken: done.slot.spotsTaken + 1, maxStudents: done.slot.maxStudents }} answer={answer} />
      {result?.credit ? (
        <p className={p.hint}>
          {w('lessonCost')}: {formatCredits(result.credit.credits, decimalSeparatorFor(locale))} {w('creditsUnit')}
        </p>
      ) : null}
      <div className={s.actions}>
        <Link className={s.linkAction} to={familyPortalSchedulePath()}>{w('mySchedule')}</Link>
        <Button onClick={onDone}>{w('done')}</Button>
      </div>
    </section>
  );
}
